import React, { useState, useEffect, useCallback, memo } from 'react';
import Layout from '../common/Layout';
import ConsistencyCalendar from '../common/ConsistencyCalendar';
import PerformanceHeroKPIs from './PerformanceHeroKPIs';
import PortfolioPerformanceChart from './PortfolioPerformanceChart';
import MonthlyPnLTable from './MonthlyPnLTable';
import PositionPnLTable from './PositionPnLTable';
import ReturnsComparisonTable from './ReturnsComparisonTable';
import PortfolioTabNavigation from './PortfolioTabNavigation';
import StrategyReviewButton from './StrategyReviewButton';
import StrategyReviewModal from './StrategyReviewModal';
import PortfolioLensToggle from '../common/PortfolioLensToggle';
import { usePortfolioLens } from '../../contexts/PortfolioLensContext';
import { apiService } from '../../services/api';
import {
  PerformancePage, PerformancePeriod, PerformanceAggregation, OutcomeType, TradesBlock, RegimesBlock,
  DailyReturnPoint,
} from '../../types/performance';
import {
  fmtUsd, fmtUsdShort, fmtUsdShortSigned, fmtPct, fmtShare, fmtRatio, fmtNum, fmtExpiry, fmtSessionLong, toneOf,
} from '../../utils/performanceFormat';

/**
 * Performance page (gold v2 phase 4b). Every number comes computed from
 * GET /api/portfolio/performance-summary (gold.portfolio_period_kpis /
 * portfolio_daily / position_daily via gold_history_read). The page formats
 * and lays out; it does not sum, annualise, rebase, classify or date-shift
 * (P5). Unknown values read "—" (P3).
 */

const GAIN = 'var(--tk-gain)';
const LOSS = 'var(--tk-loss)';
// SVG presentation attributes get literal hex (mirrors --tk-* in trader-theme.css).
const SVG = {
  gain: '#5cc79a',
  loss: '#e08a7e',
  t1: '#e7e8ea',
  t2: '#a2a7ae',
  t3: '#6f747c',
  s1: '#16171b',
  line: 'rgba(255, 255, 255, 0.08)',
  lineStrong: 'rgba(255, 255, 255, 0.16)',
};

const BAND_LABELS: Record<string, { label: string; range: string }> = {
  extreme: { label: 'Extreme', range: '> 3× avg' },
  outlier: { label: 'Outlier', range: '1.5–3× avg' },
  standard: { label: 'Standard', range: '0.5–1.5× avg' },
  grind: { label: 'The Grind', range: '≤ 0.5× avg' },
};

/** Stock -> option -> total waterfall for an outcome card (dollar sums from gold). */
const BucketWaterfall: React.FC<{ stock: number; option: number; total: number }> = ({ stock, option, total }) => {
  // geometry only: place the three given amounts on one axis
  const lo = Math.min(0, stock, total);
  const hi = Math.max(0, stock, total);
  const pad = (hi - lo || 1) * 0.06;
  const rMin = lo - pad;
  const range = (hi + pad) - rMin;
  const at = (v: number) => ((v - rMin) / range) * 100;
  const zero = at(0);
  const stockEnd = at(stock);
  const totalPos = at(total);
  return (
    <div className="perf-wf">
      <div className="perf-wf-legend">
        <div className="perf-wf-parts">
          <span>Stock <b className={toneOf(stock)}>{fmtUsdShortSigned(stock)}</b></span>
          <span>Opt <b className={toneOf(option)}>{fmtUsdShortSigned(option)}</b></span>
        </div>
        <span>= <b className={`perf-wf-net ${toneOf(total)}`}>{fmtUsdShortSigned(total)}</b></span>
      </div>
      <div className="perf-wf-scale"><span style={{ left: `${zero}%` }}>$0</span></div>
      <div className="perf-wf-bars">
        <div className="perf-wf-track">
          <div className="perf-wf-fill" style={{ left: `${Math.min(zero, stockEnd)}%`, width: `${Math.max(Math.abs(stockEnd - zero), 0.5)}%`, background: stock >= 0 ? GAIN : LOSS }} />
        </div>
        <div className="perf-wf-track is-2">
          <div className="perf-wf-fill" style={{ left: `${Math.min(stockEnd, totalPos)}%`, width: `${Math.max(Math.abs(totalPos - stockEnd), 0.5)}%`, background: option >= 0 ? GAIN : LOSS }} />
        </div>
        <div className="perf-wf-tick" style={{ left: `${stockEnd}%` }} />
        <div className="perf-wf-zero" style={{ left: `${zero}%` }} />
        <div className="perf-wf-total" style={{ left: `${totalPos}%`, background: total >= 0 ? GAIN : LOSS }} />
        <div className="perf-wf-dia" style={{ left: `${totalPos}%`, background: total >= 0 ? GAIN : LOSS }} />
      </div>
    </div>
  );
};

/** One outcome-type card (loss cushioned / premium winner / stock winner). */
const OutcomeCard: React.FC<{ title: string; t: OutcomeType | undefined; tone: 'gain' | 'loss'; context: (c: number | null) => string }> = ({ title, t, tone, context }) => (
  <div className="perf-card">
    <div className="perf-card-h">
      <span>{title}</span>
      {t && t.count > 0 && (
        <small><span><b>{t.count}</b> trade{t.count !== 1 ? 's' : ''} &middot; <b>{fmtShare(t.share)}</b></span></small>
      )}
    </div>
    {!t || t.count === 0 ? (
      <div className="perf-oc-empty">No trades in this category</div>
    ) : (
      <>
        <div className={`perf-big perf-oc-v is-${tone}`}>{fmtUsd(t.pnl)}</div>
        {t.context != null && <div className="perf-oc-ctx">{context(t.context)}</div>}
        <BucketWaterfall stock={t.stock_pnl + t.dividends} option={t.option_pnl} total={t.pnl} />
      </>
    )}
  </div>
);

const ProfitabilityCard: React.FC<{ tr: TradesBlock }> = ({ tr }) => {
  // bar lengths only: the two given averages on one scale
  const scale = Math.max(Math.abs(tr.avg_win ?? 0), Math.abs(tr.avg_loss ?? 0)) || 1;
  const pf = tr.profit_factor;
  const pfTone = pf == null ? '' : pf >= 1.5 ? 'is-gain' : pf >= 1 ? 'is-warn' : 'is-loss';
  return (
    <div className="perf-card">
      <div className="perf-card-h">Profitability</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px', gap: '8px' }}>
        <span className={`perf-big ${toneOf(tr.expectancy)}`}>
          {fmtUsdShortSigned(tr.expectancy)}<small>/trade</small>
        </span>
        <span className="perf-pf">PF <b className={pfTone}>{pf == null ? '—' : fmtRatio(pf)}</b></span>
      </div>
      <div className="perf-lbl">Payoff ratio</div>
      <div className={`perf-big ${tr.payoff_ratio == null ? '' : tr.payoff_ratio >= 1 ? 'is-gain' : 'is-loss'}`} style={{ marginBottom: '8px' }}>
        {fmtRatio(tr.payoff_ratio)}
      </div>
      <div style={{ marginBottom: '6px' }}>
        <div className="perf-line">
          <span>Avg win</span>
          <b className="is-gain">{fmtUsdShort(tr.avg_win)}</b>
        </div>
        <div className="perf-track"><i style={{ width: `${(Math.abs(tr.avg_win ?? 0) / scale) * 100}%`, background: GAIN }} /></div>
        <div className="perf-foot n">Stock {fmtUsdShortSigned(tr.avg_win_stock_pnl)} | Prem {fmtUsdShortSigned(tr.avg_win_option_pnl)}</div>
      </div>
      <div>
        <div className="perf-line">
          <span>Avg loss</span>
          <b className="is-loss">{fmtUsdShort(tr.avg_loss)}</b>
        </div>
        <div className="perf-track"><i style={{ width: `${(Math.abs(tr.avg_loss ?? 0) / scale) * 100}%`, background: LOSS }} /></div>
        <div className="perf-foot n">Stock {fmtUsdShortSigned(tr.avg_loss_stock_pnl)} | Prem {fmtUsdShortSigned(tr.avg_loss_option_pnl)}</div>
      </div>
    </div>
  );
};

const WinRateCard: React.FC<{ tr: TradesBlock }> = ({ tr }) => {
  const wr = tr.win_rate;
  const be = tr.breakeven_win_rate;
  const beats = wr != null && be != null ? wr >= be : null;
  return (
    <div className="perf-card">
      <div className="perf-card-h">Win Rate</div>
      <div className={`perf-big ${beats == null ? '' : beats ? 'is-gain' : 'is-loss'}`} style={{ marginBottom: '8px' }}>
        {fmtShare(wr, 1)}
      </div>
      <div style={{ position: 'relative', marginBottom: '6px' }}>
        <div className="perf-track"><i style={{ width: `${(wr ?? 0) * 100}%`, background: beats === false ? LOSS : GAIN }} /></div>
        {be != null && be <= 1 && <div className="perf-be" style={{ left: `${be * 100}%` }} />}
      </div>
      <div className="perf-lbl" style={{ marginBottom: '12px' }}>BE: <span className="n">{fmtShare(be)}</span></div>
      <div className="perf-line">
        <span><b className="is-gain">{tr.wins ?? '—'}</b> wins</span>
        <span><b className="is-loss">{tr.losses ?? '—'}</b> losses</span>
        {!!tr.scratches && <span><b>{tr.scratches}</b> flat</span>}
      </div>
    </div>
  );
};

const ActivityCard: React.FC<{ tr: TradesBlock; alpha: number | null }> = ({ tr, alpha }) => (
  <div className="perf-card">
    <div className="perf-card-h">Activity</div>
    <div className="perf-kv">
      <div><div className="perf-lbl">Trades</div><div className="perf-big">{tr.trade_count}</div></div>
      <div>
        <div className="perf-lbl">Avg hold</div>
        <div className="perf-big">{tr.avg_hold_days == null ? '—' : `${fmtNum(tr.avg_hold_days)}d`}<small>({fmtShare(tr.avg_hold_dte_share)} of DTE)</small></div>
      </div>
      <div><div className="perf-lbl">Pace</div><div className="perf-big">{fmtNum(tr.trades_per_week, 1)}/wk</div></div>
      <div><div className="perf-lbl">Assignment</div><div className="perf-big">{fmtShare(tr.assignment_rate, 1)}</div></div>
      <div>
        <div className="perf-lbl" title="Portfolio return minus the S&P 500's total return, same sessions">Alpha vs SPY</div>
        <div className={`perf-big ${toneOf(alpha)}`}>{fmtPct(alpha)}</div>
      </div>
    </div>
  </div>
);

const OutcomeDistribution: React.FC<{ tr: TradesBlock; isMobile: boolean }> = ({ tr, isMobile }) => {
  const bands = tr.outcome_buckets;
  if (!bands) return null;
  const maxCount = Math.max(1, ...bands.map(b => Math.max(b.wins, b.losses)));
  return (
    <div className="perf-card">
      <div className="perf-card-h">
        <span>Outcome Distribution</span>
        <small><span>◄ Losses</span><span>Wins ►</span></small>
      </div>
      <div className="perf-bfly">
        {bands.map(b => (
          <div key={b.band} className="perf-bfly-row">
            <div className="perf-bfly-l">
              {BAND_LABELS[b.band].label}
              {!isMobile && <small>{BAND_LABELS[b.band].range}</small>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              {b.losses > 0 && (
                <div title={`${b.losses} losing trade${b.losses !== 1 ? 's' : ''}`} className="perf-bfly-bar is-loss" style={{ width: `${(b.losses / maxCount) * 45}%` }}>
                  {b.losses}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              {b.wins > 0 && (
                <div title={`${b.wins} winning trade${b.wins !== 1 ? 's' : ''}`} className="perf-bfly-bar is-gain" style={{ width: `${(b.wins / maxCount) * 45}%` }}>
                  {b.wins}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="perf-bfly-axis"><span /><div /><div /></div>
      <div className="perf-legend">
        <span><b className="is-loss">{tr.losses ?? '—'}</b> losses</span>
        <span>|</span>
        <span><b className="is-gain">{tr.wins ?? '—'}</b> wins</span>
      </div>
    </div>
  );
};

const EfficiencyMatrix: React.FC<{ tr: TradesBlock }> = ({ tr }) => {
  const points = tr.closed_legs.filter(l => l.days_held != null && l.position_return != null && l.total_pnl != null);
  if (points.length === 0) return null;
  // chart geometry only (axes fitted to the given points)
  const W = 500, H = 280;
  const pad = { top: 20, right: 30, bottom: 55, left: 55 };
  const pw = W - pad.left - pad.right;
  const ph = H - pad.top - pad.bottom;
  const maxDays = Math.max(30, ...points.map(p => p.days_held as number));
  const yExtent = Math.max(0.05, ...points.map(p => Math.abs(p.position_return as number)));
  const x = (v: number) => pad.left + (v / maxDays) * pw;
  const y = (v: number) => pad.top + ph / 2 - (Math.max(-yExtent, Math.min(yExtent, v)) / yExtent) * (ph / 2);
  const maxPnl = Math.max(1, ...points.map(p => Math.abs(p.total_pnl as number)));
  const r = (pnl: number) => 4 + (Math.abs(pnl) / maxPnl) * 10;
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(maxDays * f));
  const yTicks = [-1, -0.5, 0, 0.5, 1].map(f => f * yExtent);
  const median = tr.median_days_held;
  return (
    <div className="perf-card">
      <div className="perf-card-h"><span>Efficiency Matrix</span><small>Time vs Money</small></div>
      <svg className="perf-svg" width="100%" viewBox={`0 0 ${W} ${H}`}>
        {xTicks.map(t => <line key={`x-${t}`} x1={x(t)} y1={pad.top} x2={x(t)} y2={pad.top + ph} stroke={SVG.line} />)}
        {yTicks.map((t, i) => <line key={`y-${i}`} x1={pad.left} y1={y(t)} x2={pad.left + pw} y2={y(t)} stroke={t === 0 ? SVG.lineStrong : SVG.line} />)}
        {median != null && <line x1={x(median)} y1={pad.top} x2={x(median)} y2={pad.top + ph} stroke={SVG.t3} strokeDasharray="4,3" />}
        {points.map(p => (
          <circle key={p.position_id} cx={x(p.days_held as number)} cy={y(p.position_return as number)} r={r(p.total_pnl as number)}
            fill={p.win ? SVG.gain : SVG.loss} opacity={0.75} stroke="none">
            <title>{`${p.symbol} ${fmtExpiry(p.expiry)} $${p.strike ?? '—'}${p.strategy === 'csp' ? 'P' : 'C'}\nHeld: ${p.days_held} days\nP&L: ${fmtUsd(p.total_pnl)} (${fmtPct(p.position_return, 1)} on capital)`}</title>
          </circle>
        ))}
        {xTicks.filter(t => t > 0).map(t => <text key={`xl-${t}`} x={x(t)} y={H - 28} textAnchor="middle" fill={SVG.t3} fontSize="10">{t}d</text>)}
        <text className="is-label" x={pad.left + pw / 2} y={H - 8} textAnchor="middle" fill={SVG.t2} fontSize="11">Days Held</text>
        {yTicks.map((t, i) => <text key={`yl-${i}`} x={pad.left - 6} y={y(t) + 3} textAnchor="end" fill={SVG.t3} fontSize="10">{fmtPct(t, 0)}</text>)}
        <text className="is-label" x={12} y={pad.top + ph / 2} textAnchor="middle" fill={SVG.t2} fontSize="11" transform={`rotate(-90, 12, ${pad.top + ph / 2})`}>Return on capital</text>
      </svg>
      <div className="perf-legend">
        <span><i className="is-dot" style={{ background: GAIN }} />Win</span>
        <span><i className="is-dot" style={{ background: LOSS }} />Loss</span>
        <span>Size = $ magnitude · dashed = median hold</span>
      </div>
    </div>
  );
};

const RegimeCard: React.FC<{ rg: RegimesBlock }> = ({ rg }) => {
  const scale = Math.max(1, ...rg.bands.flatMap(b => [Math.abs(b.stock_pnl ?? 0), Math.abs(b.option_pnl ?? 0)]));
  const bar = (v: number | null, cls: string) => {
    if (v == null || v === 0) return null;
    const w = Math.max((Math.abs(v) / scale) * 45, 2);
    return (
      <div className={`perf-regime-bar ${cls}`} style={v >= 0 ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }}>
        {fmtUsdShortSigned(v)}
      </div>
    );
  };
  return (
    <div className="perf-card">
      <div className="perf-card-h">
        <span>P&L by SPY Regime</span>
        <small className="perf-legend" style={{ marginTop: 0 }}>
          <span><i style={{ background: 'var(--perf-bar)' }} />Stock</span>
          <span><i style={{ background: 'var(--perf-bar-2)' }} />Option</span>
        </small>
      </div>
      <div className="perf-bfly" style={{ gap: '6px' }}>
        {[...rg.bands].reverse().map(b => (
          <div key={b.label} className="perf-regime-row"
            title={b.days > 0 ? `Stock ${fmtUsd(b.stock_pnl)} · Option ${fmtUsd(b.option_pnl)} · Net ${fmtUsd(b.net_pnl)}` : `${b.label}: no session`}>
            <div className="perf-bfly-l">{b.label}{b.days > 0 && <small>{b.days} day{b.days !== 1 ? 's' : ''}</small>}</div>
            <div className="perf-regime-plot">
              <div className="perf-regime-mid" />
              {b.days > 0 && (<>{bar(b.stock_pnl, 'is-stock')}{bar(b.option_pnl, 'is-opt')}</>)}
            </div>
          </div>
        ))}
      </div>
      <div className="perf-legend">
        <span><b style={{ color: 'var(--tk-t1)' }}>{rg.total_days}</b> trading days</span>
        {rg.days_without_benchmark > 0 && <span>· {rg.days_without_benchmark} without an S&P 500 close</span>}
      </div>
    </div>
  );
};

const DailyScatter: React.FC<{ points: DailyReturnPoint[]; sharpe: number | null; benchmarkSharpe: number | null }> = ({ points, sharpe, benchmarkSharpe }) => {
  if (points.length === 0) return null;
  // chart geometry only
  const axisMax = Math.max(0.005, ...points.flatMap(p => [Math.abs(p.portfolio), Math.abs(p.benchmark)]));
  const size = 300, pad = 36, plot = size - pad;
  const toX = (v: number) => pad + ((v + axisMax) / (2 * axisMax)) * plot;
  const toY = (v: number) => ((axisMax - v) / (2 * axisMax)) * plot;
  const ticks = [-axisMax, -axisMax / 2, 0, axisMax / 2, axisMax];
  return (
    <div className="perf-card perf-scatter-card">
      <div className="perf-card-h"><span>Daily Returns: Portfolio vs SPY</span></div>
      <svg className="perf-svg" viewBox={`0 0 ${size} ${size}`} width="100%" style={{ overflow: 'visible', maxHeight: '400px' }}>
        <rect x={pad} y={0} width={plot} height={plot} fill={SVG.s1} rx="2" />
        {ticks.map((v, i) => (
          <g key={i}>
            <line x1={toX(v)} y1={0} x2={toX(v)} y2={plot} stroke={v === 0 ? SVG.lineStrong : SVG.line} strokeWidth={1} />
            <line x1={pad} y1={toY(v)} x2={pad + plot} y2={toY(v)} stroke={v === 0 ? SVG.lineStrong : SVG.line} strokeWidth={1} />
          </g>
        ))}
        <line x1={toX(-axisMax)} y1={toY(-axisMax)} x2={toX(axisMax)} y2={toY(axisMax)} stroke={SVG.t3} strokeWidth={1} strokeDasharray="4 3" />
        {points.map(p => (
          <circle key={p.date} cx={toX(p.portfolio)} cy={toY(p.benchmark)} r={3} fill={p.beat ? SVG.gain : SVG.loss} opacity={0.7}>
            <title>{`${p.date}  SPY: ${fmtPct(p.benchmark)}  Portfolio: ${fmtPct(p.portfolio)}`}</title>
          </circle>
        ))}
        {[-axisMax, 0, axisMax].map((v, i) => (
          <g key={`label-${i}`}>
            <text x={toX(v)} y={plot + 14} textAnchor="middle" fill={SVG.t3} fontSize="9">{fmtPct(v, 1)}</text>
            <text x={pad - 4} y={toY(v) + 3} textAnchor="end" fill={SVG.t3} fontSize="9">{fmtPct(v, 1)}</text>
          </g>
        ))}
        <text className="is-label" x={pad + plot / 2} y={plot + 28} textAnchor="middle" fill={SVG.t2} fontSize="10">Portfolio %</text>
        <text className="is-label" x={12} y={plot / 2} textAnchor="middle" fill={SVG.t2} fontSize="10" transform={`rotate(-90, 12, ${plot / 2})`}>SPY %</text>
      </svg>
      <div className="perf-legend">
        <span><i className="is-dot" style={{ background: GAIN }} />Beat SPY</span>
        <span><i className="is-dot" style={{ background: LOSS }} />Lagged SPY</span>
        <span><i className="is-dash" />x=y</span>
      </div>
      <div className="perf-sharpes">
        <div><span>Portfolio Sharpe</span><b>{fmtRatio(sharpe, 2, '')}</b></div>
        <hr />
        <div><span>SPY Sharpe</span><b style={{ color: 'var(--tk-t2)' }}>{fmtRatio(benchmarkSharpe, 2, '')}</b></div>
      </div>
    </div>
  );
};

const PortfolioPerformance: React.FC = () => {
  // CC/CSP portfolio lens — drives a full server-side refetch so every number on
  // the page (KPIs, benchmark, stats, tables, totals) reflects the same subset.
  const { lens } = usePortfolioLens();
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PerformancePeriod>('YTD');
  const [aggregation, setAggregation] = useState<PerformanceAggregation>('monthly');
  const [showBenchmark, setShowBenchmark] = useState<boolean>(false);
  const [page, setPage] = useState<PerformancePage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Strategy Review state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewText, setReviewText] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewCached, setReviewCached] = useState(false);
  const [reviewConversationId, setReviewConversationId] = useState<number | null>(null);

  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setLoadError(null);
    apiService.getPerformanceSummary(selectedPeriod, aggregation, lens)
      .then(data => { if (!abort.signal.aborted) setPage(data); })
      .catch(err => {
        if (!abort.signal.aborted) {
          console.error('[PortfolioPerformance] load failed', err);
          setLoadError('Performance data could not be loaded.');
        }
      })
      .finally(() => { if (!abort.signal.aborted) setLoading(false); });
    return () => abort.abort();
  }, [selectedPeriod, aggregation, lens]);

  const handleFetchStrategyReview = useCallback((forceRefresh = false) => {
    setReviewLoading(true);
    setReviewError(null);
    if (forceRefresh) {
      setReviewText(null);
      setReviewConversationId(null);
    }
    setShowReviewModal(true);
    let accumulated = '';
    // the backend reads the same gold figures itself; nothing is sent from the page
    apiService.streamStrategyReview(
      selectedPeriod,
      lens,
      forceRefresh,
      (chunk) => { accumulated += chunk; setReviewText(accumulated); },
      ({ cached, conversationId }) => {
        setReviewCached(cached);
        setReviewLoading(false);
        if (conversationId !== undefined) setReviewConversationId(conversationId);
      },
      (error) => { setReviewError(error); setReviewLoading(false); },
    );
  }, [selectedPeriod, lens]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const hero = page?.hero ?? null;
  const tr = page?.trades ?? null;
  const types = tr?.outcome_types ?? null;

  return (
    <Layout>
      <div className="app-page-container perf">
        <PortfolioTabNavigation />

        <div className="app-section perf-top">
          <div className="perf-bar">
            <PortfolioLensToggle />
            <div className="perf-bar-r period-selector">
              <div className="seg" role="group" aria-label="Period">
                {(['1M', '3M', '6M', 'YTD', '1Y', 'All'] as const).map((period) => (
                  <button key={period} type="button" aria-pressed={selectedPeriod === period}
                    onClick={() => { setSelectedPeriod(period); setAggregation('monthly'); }}>
                    {period}
                  </button>
                ))}
              </div>
              <StrategyReviewButton onClick={() => handleFetchStrategyReview()} isLoading={reviewLoading} disabled={loading} />
            </div>
          </div>
          {!loading && page?.window && (
            <div className="perf-asof">Data as of market close {fmtSessionLong(page.window.end)}</div>
          )}
          {!loading && loadError && <div className="perf-asof">{loadError}</div>}
        </div>

        <PerformanceHeroKPIs hero={hero} window={page?.window ?? null} loading={loading} />

        {/* SECTION 1: INCOME */}
        <div className="app-section">
          <div className="perf-h">
            <h3>Income</h3>
            <p>Are you generating premium income?</p>
          </div>
          <div className="perf-gran">
            <span className="perf-gran-l">Granularity</span>
            <div className="seg" role="group" aria-label="Granularity">
              {([['daily', 'Day'], ['weekly', 'Week'], ['monthly', 'Month']] as [PerformanceAggregation, string][]).map(([value, label]) => (
                <button key={value} type="button" aria-pressed={aggregation === value} onClick={() => setAggregation(value)}>{label}</button>
              ))}
            </div>
            <button type="button" className="perf-toggle" aria-pressed={showBenchmark} onClick={() => setShowBenchmark(prev => !prev)}>vs SPY</button>
          </div>
          <MonthlyPnLTable income={page?.income ?? null} selectedPeriod={selectedPeriod} showBenchmark={showBenchmark} />
        </div>

        {/* SECTION 2: STOCK SELECTION */}
        <div className="app-section">
          <div className="perf-h">
            <h3>Stock Selection</h3>
            <p>Did you pick the right stocks?</p>
          </div>
          <PositionPnLTable data={page?.stock_selection ?? null} selectedPeriod={selectedPeriod} />
        </div>

        {/* SECTION 3: STRATEGY IMPACT */}
        {page && page.chart.dates.length > 0 && (
          <div className="app-section">
            <div className="perf-h">
              <h3>Strategy Impact</h3>
              <p>Is the covered call overlay helping?</p>
            </div>
            <PortfolioPerformanceChart data={page.chart} sharpe={hero?.sharpe ?? null} benchmarkSharpe={hero?.benchmark_sharpe ?? null} />
            <div className="perf-returns">
              <ReturnsComparisonTable
                rows={page.returns_table}
                selectedPeriod={selectedPeriod}
                onPeriodSelect={(period) => { setSelectedPeriod(period as PerformancePeriod); setAggregation('monthly'); }}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* SECTION 4: TRADE ANALYTICS */}
        <div className="app-section">
          <div className="perf-h">
            <h3>Trade Analytics</h3>
            <p>How can you improve execution?</p>
          </div>
          {loading ? (
            <div className="perf-state">Loading trading statistics...</div>
          ) : !tr || tr.trade_count === 0 ? (
            <div className="perf-state">
              No closed positions in this period. Close your first covered call position to see your trading statistics, win rate, and performance metrics.
            </div>
          ) : (
            <div className="trading-stats-bento perf-stats">
              {tr.trades_pnl_unknown > 0 && (
                <div className="perf-state">
                  {tr.trades_pnl_unknown} trade{tr.trades_pnl_unknown !== 1 ? 's' : ''} without a recorded close price: their win or loss is unknown, so the win rate, averages and outcome charts are blank for this period.
                </div>
              )}
              <div className="perf-row3">
                <ProfitabilityCard tr={tr} />
                <WinRateCard tr={tr} />
                <ActivityCard tr={tr} alpha={hero?.alpha ?? null} />
              </div>
              <div className="perf-row2">
                <OutcomeDistribution tr={tr} isMobile={isMobile} />
                <EfficiencyMatrix tr={tr} />
              </div>
              {types && (
                <div className="perf-row3">
                  <OutcomeCard title="Loss Cushioned" t={types.loss_cushioned} tone="loss"
                    context={(c) => `Premium offset ${fmtShare(c)} of the stock decline`} />
                  <OutcomeCard title="Premium Winner" t={types.premium_winner} tone="gain"
                    context={(c) => `Option made ${fmtShare(c)} of the gain`} />
                  <OutcomeCard title="Stock Winner" t={types.stock_winner} tone="gain"
                    context={(c) => `The call gave back ${fmtShare(c)} of the stock gain`} />
                </div>
              )}
              {page?.regimes && page.regimes.total_days > 0 && (
                <div className="perf-row2">
                  <RegimeCard rg={page.regimes} />
                  <DailyScatter points={page.daily_returns} sharpe={page.regimes.sharpe} benchmarkSharpe={page.regimes.benchmark_sharpe} />
                </div>
              )}
            </div>
          )}
        </div>

        {page && page.calendar.weeks.length > 0 && <ConsistencyCalendar calendar={page.calendar} />}
      </div>

      <StrategyReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onRefresh={() => handleFetchStrategyReview(true)}
        isLoading={reviewLoading}
        brief={reviewText}
        error={reviewError}
        cached={reviewCached}
        selectedPeriod={selectedPeriod}
        stats={{
          totalPnl: hero?.pnl ?? null,
          stockPnl: hero?.stock_pnl ?? null,
          optionPnl: hero?.option_pnl ?? null,
          premiumIncome: hero?.premium_income ?? null,
          costOfCappedUpside: hero?.cost_of_capped_upside ?? null,
          dividends: hero?.dividends ?? null,
          spyReturn: hero?.benchmark_return ?? null,
          roaic: hero?.roaic ?? null,
          incomeYield: hero?.income_yield ?? null,
          lossesCushionedShare: types?.loss_cushioned?.share ?? null,
          lossesCushionedContext: types?.loss_cushioned?.context ?? null,
        }}
        conversationId={reviewConversationId}
      />
    </Layout>
  );
};

export default memo(PortfolioPerformance);
